import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTitle } from "@/lib/format";
import { PageLoader } from "@/components/common";
import HoursEditor, { DEFAULT_HOURS } from "@/components/HoursEditor";
import { DetailsFields, LocationFields, ImagesFields, EMPTY_BUSINESS, validateBusiness, validateLocation, pickBusiness } from "@/components/provider/BusinessFields";
import { AccountStep, Submitted, ServicesStep } from "@/components/provider/ListBusinessSteps";

const STEPS = ["Business", "Location & hours", "Services", "Images", "Submit"];

const stepClass = (i, step) => {
  if (i === step) return "bg-sky-600 text-white";
  if (i < step) return "bg-teal-50 text-teal-700";
  return "bg-slate-100 text-slate-500";
};

function validateStep(step, biz, services) {
  if (step === 0) return validateBusiness(biz);
  if (step === 1) return validateLocation(biz);
  if (step === 2 && !services.length) return "Add at least one service";
  return null;
}

function ExistingProfile({ existing }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="lyb-existing">
      <p className="font-heading text-xl font-semibold">You already have a business profile ({existing.status}).</p>
      {existing.status === "pending" && <p className="mt-2 text-slate-600">Your business profile has been submitted for review.</p>}
      <Link to="/provider-dashboard" className="mt-5 inline-flex h-12 items-center rounded-xl bg-sky-600 px-6 font-semibold text-white" data-testid="lyb-dashboard-link">Open Provider Dashboard</Link>
    </div>
  );
}

function ReviewStep({ biz, categories, services, hours }) {
  return (
    <div className="space-y-2 text-sm text-slate-700" data-testid="lyb-review">
      <p><b>{biz.business_name}</b> · {categories.find((c) => c.id === biz.category_id)?.name}</p>
      <p>{biz.suburb}, {biz.city} {biz.postcode}</p>
      <p>{services.length} service(s) · {Object.values(hours).filter((h) => h.enabled).length} working days</p>
      <p className="pt-3 text-slate-500">After submission, an admin will review your profile before it appears publicly.</p>
    </div>
  );
}

function WizardStepBody({ step, biz, setBiz, hours, setHours, services, setServices, categories }) {
  if (step === 0) return <DetailsFields value={biz} onChange={setBiz} categories={categories} />;
  if (step === 1) {
    return (
      <div className="space-y-6">
        <LocationFields value={biz} onChange={setBiz} />
        <div><p className="mb-3 text-sm font-semibold">Business hours (determines bookable times)</p><HoursEditor value={hours} onChange={setHours} /></div>
      </div>
    );
  }
  if (step === 2) return <ServicesStep services={services} setServices={setServices} />;
  if (step === 3) return <ImagesFields value={biz} onChange={setBiz} />;
  return <ReviewStep biz={biz} categories={categories} services={services} hours={hours} />;
}

function WizardNav({ step, busy, onBack, onNext, onSubmit }) {
  return (
    <div className="mt-8 flex gap-3 border-t border-slate-100 pt-6">
      {step > 0 && <button data-testid="lyb-back" onClick={onBack} className="h-12 rounded-xl border border-slate-300 px-6 font-semibold text-slate-700">Back</button>}
      {step < 4 && <button data-testid="lyb-next" onClick={onNext} className="h-12 flex-1 rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700">Continue</button>}
      {step === 4 && (
        <button data-testid="lyb-submit" disabled={busy} onClick={onSubmit} className="flex h-12 flex-1 items-center justify-center rounded-xl bg-teal-600 font-semibold text-white hover:bg-teal-700">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit for approval"}
        </button>
      )}
    </div>
  );
}

function Wizard({ user, existing, categories, onDone }) {
  const { refreshUser } = useAuth();
  const rejected = existing?.status === "rejected";
  const [step, setStep] = useState(0);
  const [biz, setBiz] = useState(() => (rejected ? pickBusiness(existing) : { ...EMPTY_BUSINESS, owner_name: user.name, email: user.email, phone: user.phone || "" }));
  const [hours, setHours] = useState(rejected ? existing.working_hours : DEFAULT_HOURS);
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);

  const next = () => {
    const err = validateStep(step, biz, services);
    if (err) return toast.error(err);
    setStep(step + 1);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const cleanServices = services.map(({ _key, ...s }) => s);
      await api.post("/provider/application", { ...biz, years_in_business: Number(biz.years_in_business), working_hours: hours, services: cleanServices });
      await refreshUser();
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
      {rejected && <p className="mb-5 rounded-xl bg-rose-50 p-4 text-sm text-rose-700">Your previous application was not approved{existing.review_note ? `: ${existing.review_note}` : "."} Update and resubmit below.</p>}
      <div className="mb-8 flex gap-2 overflow-x-auto no-scrollbar" data-testid="lyb-stepper">
        {STEPS.map((s, i) => <span key={s} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${stepClass(i, step)}`}>{i + 1}. {s}</span>)}
      </div>
      <WizardStepBody step={step} biz={biz} setBiz={setBiz} hours={hours} setHours={setHours} services={services} setServices={setServices} categories={categories} />
      <WizardNav step={step} busy={busy} onBack={() => setStep(step - 1)} onNext={next} onSubmit={submit} />
    </div>
  );
}

function ListBusinessContent({ user, existing, categories, done, onDone }) {
  if (done) return <Submitted />;
  if (!user) return <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"><AccountStep /></div>;
  if (user.role === "admin") return <p className="rounded-xl bg-slate-100 p-6 text-slate-600">Admin accounts cannot list a business.</p>;
  if (existing && existing.status !== "rejected") return <ExistingProfile existing={existing} />;
  return <Wizard user={user} existing={existing} categories={categories} onDone={onDone} />;
}

export default function ListBusiness() {
  useTitle("List Your Business");
  const { user } = useAuth();
  const [existing, setExisting] = useState(undefined);
  const [categories, setCategories] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);
  useEffect(() => {
    if (!user) { setExisting(null); return; }
    api.get("/provider/me").then(({ data }) => setExisting(data.provider)).catch(() => setExisting(null));
  }, [user]);

  if (user === null || (user && existing === undefined)) return <PageLoader />;
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="list-business-page">
      <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-teal-600"><Store className="h-4 w-4" />For businesses</p>
      <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">List Your Business</h1>
      <p className="mt-2 text-slate-600">Reach new customers, showcase your services, and manage bookings in one place.</p>
      <div className="mt-8"><ListBusinessContent user={user} existing={existing} categories={categories} done={done} onDone={() => setDone(true)} /></div>
    </div>
  );
}
